<template>
  <div
    ref="root"
    :class="baseClass"
    :data-expanded="expanded"
    :data-loading="loading"
    :data-position="position"
  >
    <input
      ref="input"
      type="text"
      :class="`${baseClass}-input`"
      :value="value"
      role="combobox"
      autocomplete="off"
      autocapitalize="off"
      autocorrect="off"
      spellcheck="false"
      aria-autocomplete="list"
      aria-haspopup="listbox"
      :aria-owns="resultListId"
      :aria-expanded="expanded ? 'true' : 'false'"
      :aria-activedescendant="activeDescendant"
      v-bind="$attrs"
      @input="handleInput"
      @keydown="handleKeyDown"
      @focus="handleFocus"
      @blur="handleBlur"
    >
    <ul
      ref="resultList"
      :id="resultListId"
      :class="`${baseClass}-result-list`"
      role="listbox"
      @mousedown="handleResultMouseDown"
      @click="handleResultClick"
    >
      <template v-for="(result, index) in results" :key="resultProps[index].id">
        <slot name="result" :result="result" :props="resultProps[index]">
          <li v-bind="resultProps[index]">{{ getResultValue(result) }}</li>
        </slot>
      </template>
    </ul>
  </div>
</template>

<script>
// A small, Vue-3-native autocomplete that replaces @trevoreyre/autocomplete-vue
// (a Vue-2-only library that only ran under @vue/compat). It reproduces the
// pieces our event search relies on — the `base-class`-derived class names, the
// `#result` scoped slot, the `@submit` event, keyboard navigation, ARIA wiring,
// and above/below positioning — but leaves visibility/positioning to
// `event_autocomplete_search.scss` (keyed on `[data-position]`, `[data-loading]`
// and the input's `aria-expanded`) rather than the object-form inline `style`
// the old library bound through a spread `v-bind`, which @vue/compat silently
// dropped (see PR #1472).

let idCounter = 0;
const uniqueId = prefix => `${prefix}${(idCounter += 1)}`;

// Places the result list below the input unless there isn't room below, there
// is more room above, and it would fit above. Mirrors the old library so the
// `[data-position]`-driven border radii stay correct.
const relativePosition = (input, list) => {
  const inputBox = input.getBoundingClientRect();
  const listBox = list.getBoundingClientRect();
  const above =
    inputBox.bottom + listBox.height > window.innerHeight &&
    window.innerHeight - inputBox.bottom < inputBox.top &&
    window.pageYOffset + inputBox.top - listBox.height > 0;
  return above ? 'above' : 'below';
};

export default {
  name: 'Autocomplete',
  inheritAttrs: false,

  props: {
    search: { type: Function, required: true },
    baseClass: { type: String, default: 'autocomplete' },
    autoSelect: { type: Boolean, default: false },
    getResultValue: { type: Function, default: result => result },
    defaultValue: { type: String, default: '' }
  },

  emits: ['submit'],

  data() {
    return {
      value: this.defaultValue,
      resultListId: uniqueId(`${this.baseClass}-result-list-`),
      results: [],
      selectedIndex: -1,
      expanded: false,
      loading: false,
      position: 'below',
      resetPosition: true,
      searchCounter: 0
    };
  },

  computed: {
    activeDescendant() {
      return this.selectedIndex > -1 ? this.resultProps[this.selectedIndex].id : '';
    },

    resultProps() {
      return this.results.map((result, index) => ({
        id: `${this.baseClass}-result-${index}`,
        class: `${this.baseClass}-result`,
        'data-result-index': index,
        role: 'option',
        ...(this.selectedIndex === index ? { 'aria-selected': 'true' } : {})
      }));
    }
  },

  mounted() {
    document.body.addEventListener('click', this.handleDocumentClick);
  },

  beforeUnmount() {
    document.body.removeEventListener('click', this.handleDocumentClick);
  },

  updated() {
    if (!this.$refs.input || !this.$refs.resultList) return;

    // Decide above/below once per open, the first render that has results.
    if (this.resetPosition && this.results.length > 0) {
      this.resetPosition = false;
      this.position = relativePosition(this.$refs.input, this.$refs.resultList);
    }

    this.scrollSelectedIntoView();
  },

  methods: {
    updateResults(value) {
      const search = (this.searchCounter += 1);
      this.loading = true;

      Promise.resolve(this.search(value))
        .then(results => {
          if (search !== this.searchCounter) return; // a newer search superseded this one
          this.loading = false;
          this.results = results || [];

          if (this.results.length === 0) {
            this.hideResults();
            return;
          }

          this.selectedIndex = this.autoSelect ? 0 : -1;
          this.expanded = true;
        })
        .catch(() => {
          if (search !== this.searchCounter) return;
          this.loading = false;
        });
    },

    hideResults() {
      this.selectedIndex = -1;
      this.results = [];
      this.expanded = false;
      this.resetPosition = true;
    },

    setValue(result) {
      this.value = result ? this.getResultValue(result) : '';
    },

    selectResult() {
      const selected = this.results[this.selectedIndex];
      if (selected) this.setValue(selected);
      this.hideResults();
    },

    scrollSelectedIntoView() {
      const list = this.$refs.resultList;
      const selected = list.querySelector(`[data-result-index="${this.selectedIndex}"]`);
      if (!selected) return;

      const listBox = list.getBoundingClientRect();
      const selectedBox = selected.getBoundingClientRect();
      if (selectedBox.top < listBox.top) {
        list.scrollTop -= listBox.top - selectedBox.top;
      } else if (selectedBox.bottom > listBox.bottom) {
        list.scrollTop += selectedBox.bottom - listBox.bottom;
      }
    },

    moveSelection(delta) {
      const count = this.results.length;
      if (count === 0) return;
      const next = this.selectedIndex + delta;
      // Wrap around at both ends.
      this.selectedIndex = ((next % count) + count) % count;
    },

    handleInput(event) {
      this.value = event.target.value;
      this.updateResults(this.value);
    },

    handleFocus(event) {
      this.value = event.target.value;
      this.updateResults(this.value);
    },

    handleBlur() {
      this.hideResults();
    },

    handleKeyDown(event) {
      switch (event.key) {
        case 'Up': // IE/Edge
        case 'ArrowUp':
          event.preventDefault();
          this.moveSelection(-1);
          break;
        case 'Down': // IE/Edge
        case 'ArrowDown':
          event.preventDefault();
          this.moveSelection(1);
          break;
        case 'Tab':
          this.selectResult();
          break;
        case 'Enter': {
          const selected = this.results[this.selectedIndex];
          this.selectResult();
          this.$emit('submit', selected);
          break;
        }
        case 'Esc': // IE/Edge
        case 'Escape':
          this.hideResults();
          this.setValue();
          break;
        default:
      }
    },

    handleResultMouseDown(event) {
      // Keep focus on the input so the click lands before blur hides the list.
      event.preventDefault();
    },

    handleResultClick(event) {
      const resultEl = event.target.closest('[data-result-index]');
      if (!resultEl) return;

      this.selectedIndex = parseInt(resultEl.dataset.resultIndex, 10);
      const selected = this.results[this.selectedIndex];
      this.selectResult();
      this.$emit('submit', selected);
    },

    handleDocumentClick(event) {
      if (this.$refs.root.contains(event.target)) return;
      this.hideResults();
    }
  }
};
</script>
